import { Badge, Box, Heading, Link } from "@chakra-ui/layout";
import {
  useColorModeValue,
  Stack,
  VStack,
  HStack,
  Button,
  Input,
  IconButton,
  FormControl,
  FormLabel,
  Image,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { Field, FieldArray, Form, Formik } from "formik";
import { InputControl, SelectControl, SubmitButton } from "formik-chakra-ui";
import React, { FC, useMemo, useState, useEffect, useRef } from "react";
import TagInput from "components/TagInput";
import { useMutation, useQueryClient } from "react-query";
import { updateMCQSQuestion } from "API/questionApi";
import { Link as RoLink } from "react-router-dom";
import { GoTrashcan } from "react-icons/go";
import { BiCheckbox, BiCheckboxChecked } from "react-icons/bi"; // Corrected: Added missing import
import * as Yup from "yup";
import { STATIC_PREFIX } from "utils/constants";

// --- INTERFACES AND UTILITIES ---

interface IMCQSQuestion {
  _id: string;
  question: string;
  positiveMarks?: number;
  negativeMarks?: number;
  answerJustification?: string;
  tags: string[];
  level: "easy" | "normal" | "difficult";
  mcqSOptions: string[];
  mcqSAnswer: number;
  mcqSOptionImages?: string[];
  questionImg?: string;
  justificationImg?: string;
  subjectName: string;
  isPremium: boolean;
  type: string;
}

interface UpdateQuestionMCQSProps {
  question: IMCQSQuestion;
  role: string | null | undefined;
}


const getBackendBaseUrl = () => {
  if (typeof window !== "undefined") {
    return window.location.origin.replace(/:3000$/, ":9000").replace(/\/+$/, "");
  }
  return "";
};

const buildStoredImageUrl = (val?: string): string => {
  if (!val || typeof val !== "string" || val.startsWith('http')) return val || "";
  const base = getBackendBaseUrl();
  return `${base}${STATIC_PREFIX}/${encodeURIComponent(val)}`;
};

const MAX_FILE_BYTES = 2 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png"];

const getFileError = (file: File | null) => {
  if (!file) return null;
  if (!ALLOWED_TYPES.includes(file.type)) return "Only JPG or PNG files allowed.";
  if (file.size > MAX_FILE_BYTES) return "File must be 2 MB or less.";
  return null;
};

// --- IMPROVED OPTION INPUT COMPONENT ---

interface OptionInputProps {
  value: string;
  onChange: (value: string) => void;
  onImageChange: (file: File | null) => void;
  imageFile: File | null;
  imageUrl?: string;
  imageError: string | null;
  placeholder?: string;
}

const OptionInput: React.FC<OptionInputProps> = ({
  value,
  onChange,
  onImageChange,
  imageFile,
  imageUrl,
  imageError,
  placeholder = "Enter option text or paste image"
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const hasNewImage = !!imageFile;
  const hasExistingImage = !!imageUrl && !hasNewImage;
  const hasImage = hasNewImage || hasExistingImage;

  const previewUrl = useMemo(() => {
    if (hasNewImage) return URL.createObjectURL(imageFile);
    if (hasExistingImage) return imageUrl;
    return null;
  }, [imageFile, imageUrl, hasNewImage, hasExistingImage]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerText !== value) {
      editorRef.current.innerText = value || "";
    }
  }, [value]);

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const file = e.clipboardData.files[0];
    if (file && file.type.startsWith('image/')) {
      e.preventDefault();
      if (getFileError(file)) {
        return;
      }
      onImageChange(file);
    }
  };

  const imagePreview = useMemo(() => {
    if (!hasImage) return null;
    return (
      <HStack mt={2} spacing={3} align="center">
        {previewUrl && (
          <Image
            src={previewUrl}
            alt="Option preview"
            maxH="60px" maxW="80px" objectFit="contain"
            borderWidth="1px" borderRadius="md"
            onClick={onOpen} cursor="pointer"
          />
        )}
        <Button size="sm" onClick={onOpen}>Preview</Button>
        <IconButton
          aria-label="Remove image"
          icon={<GoTrashcan />}
          size="sm"
          colorScheme="red"
          onClick={() => onImageChange(null)}
        />
        <Text fontSize="sm" color="gray.600">Image attached</Text>
      </HStack>
    );
  }, [hasImage, previewUrl, onOpen, onImageChange]);

  return (
    <Box>
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange((e.currentTarget as HTMLDivElement).innerText)}
        onPaste={handlePaste}
        p={2}
        borderWidth="1px"
        borderRadius="md"
        minH="40px"
        whiteSpace="pre-wrap"
        outline="none"
        _focusWithin={{ boxShadow: "outline" }}
        aria-label={placeholder}
      />
      {imagePreview}
      {imageError && (
        <Text color="red.400" fontSize="sm" mt={1}>{imageError}</Text>
      )}
      {!hasImage && (!value || value.trim() === '') && (
        <Text mt={1} fontSize="xs" color="gray.500">Type here or press Ctrl+V to paste an image</Text>
      )}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={4}>
            {hasImage && <Image src={previewUrl!} alt="Option full preview" maxH="70vh" objectFit="contain" mx="auto" />}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// --- MAIN UPDATE COMPONENT ---

const UpdateQuestionMCQS: FC<UpdateQuestionMCQSProps> = ({ question, role }) => {
  const toast = useToast();
  const cache = useQueryClient();

  const [mcqSAnswer, setMcqSAnswer] = useState<number>(question.mcqSAnswer);
  const [tags, setTags] = useState<string[]>([...(question.tags || [])]);
  const [questionImageFile, setQuestionImageFile] = useState<File | null>(null);
  const [justificationImageFile, setJustificationImageFile] = useState<File | null>(null);

  const [optionImages, setOptionImages] = useState<(File | null)[]>([]);
  const [optionImageUrls, setOptionImageUrls] = useState<string[]>([]);
  const [removedOptionImageIndices, setRemovedOptionImageIndices] = useState<number[]>([]);

  useEffect(() => {
    setMcqSAnswer(question.mcqSAnswer);
    setTags([...(question.tags || [])]);

    const initialUrls = (question.mcqSOptionImages || []).map(img => buildStoredImageUrl(img));
    setOptionImageUrls(initialUrls);
    setOptionImages(new Array(question.mcqSOptions?.length || 0).fill(null));
    setRemovedOptionImageIndices([]);
  }, [question]);

  const { isLoading, mutateAsync } = useMutation(updateMCQSQuestion, {
    onSuccess: () => {
      cache.invalidateQueries(["question", question._id]);
      toast({ title: "Question Updated Successfully", status: "success", duration: 4000, isClosable: true });
    },
    onError: (err: any) => {
      const msg = err?.message || "Something went wrong.";
      toast({ title: "Error", description: msg, status: "error", duration: 4000, isClosable: true });
    },
  });

  const initialValues = useMemo(() => ({
    question: question.question || "",
    positiveMarks: question.positiveMarks ?? 2,
    negativeMarks: question.negativeMarks ?? -1,
    answerJustification: question.answerJustification || "",
    level: question.level || "normal",
    mcqSOptions: question.mcqSOptions?.map(opt => opt.replace(/\[IMAGE:[^\]]+\]/g, '').trim()) || [],
  }), [question]);

  const validationSchema = Yup.object({
    question: Yup.string().required("Question is required"),
    positiveMarks: Yup.number().typeError("Must be a number").required("Required"),
    negativeMarks: Yup.number().typeError("Must be a number").required("Required"),
  });

  return (
    <>
      <Heading size="md" mb="4">
        <Link as={RoLink} to={`/${role}/dashboard/questions`} color="teal.500">Questions</Link> / Update MCQS
      </Heading>

      <HStack justify="space-between" mb={4}>
        <Badge>{question.subjectName}</Badge>
        {question.isPremium && <Badge colorScheme="yellow">Premium</Badge>}
      </HStack>

      <Box my="20px" border="1px" borderColor={useColorModeValue("gray.100", "gray.700")} borderRadius="md">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={async (values) => {
            const form = new FormData();
            form.append("_id", question._id);
            form.append("type", question.type);
            form.append("question", values.question);
            form.append("positiveMarks", String(values.positiveMarks));
            form.append("negativeMarks", String(values.negativeMarks));
            form.append("answerJustification", values.answerJustification);
            form.append("level", values.level);
            tags.forEach((t) => form.append("tags[]", t));

            values.mcqSOptions.forEach(opt => form.append("mcqSOptions[]", opt));
            form.append("mcqSAnswer", String(mcqSAnswer));

            if (questionImageFile) form.append("questionImageFile", questionImageFile);
            if (justificationImageFile) form.append("justificationImageFile", justificationImageFile);

            optionImages.forEach((file, index) => {
              if (file) {
                form.append("mcqSOptionImages[]", file, file.name || `option_${index}.png`);
                form.append("mcqSOptionImageIndices[]", String(index));
              }
            });

            removedOptionImageIndices.forEach(index => {
              form.append("removedOptionImageIndices[]", String(index));
            });

            await mutateAsync(form as any);
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Stack direction={["column", "row"]} spacing="20px">
                <VStack w={{ base: "100%", lg: "30%" }} spacing="15px" padding="20px">
                  <SelectControl isRequired name="level" label="Level" selectProps={{ rounded: "md" }}>
                    <option value="easy">Easy</option>
                    <option value="normal">Normal</option>
                    <option value="difficult">Difficult</option>
                  </SelectControl>
                  <FormControl>
                    <FormLabel>Tags</FormLabel>
                    <TagInput tags={tags} setTags={setTags} />
                  </FormControl>
                </VStack>

                <Box w={{ base: "100%", lg: "70%" }} padding="20px">
                  <VStack align="start" spacing={4}>
                    <InputControl label="Question" name="question" />

                    <HStack w="100%" spacing={4}>
                      <InputControl label="Positive Marks" name="positiveMarks" inputProps={{ type: "number" }} />
                      <InputControl label="Negative Marks" name="negativeMarks" inputProps={{ type: "number" }} />
                    </HStack>

                    <FieldArray name="mcqSOptions">
                      {(arrayHelpers) => (
                        <VStack align="stretch" w="100%">
                          <FormLabel>Options</FormLabel>
                          {values.mcqSOptions.map((_, index) => (
                            <HStack key={index} align="flex-start" spacing={2}>
                              <Box pt={2}>
                                <IconButton
                                  aria-label={`Select option ${index + 1}`}
                                  icon={mcqSAnswer === index ? <BiCheckboxChecked /> : <BiCheckbox />}
                                  onClick={() => setMcqSAnswer(index)}
                                  colorScheme={mcqSAnswer === index ? "teal" : "gray"}
                                  variant="ghost"
                                />
                              </Box>
                              <Box flex="1">
                                <OptionInput
                                  value={values.mcqSOptions[index]}
                                  onChange={(text) => setFieldValue(`mcqSOptions.${index}`, text)}
                                  imageFile={optionImages[index]}
                                  imageUrl={optionImageUrls[index]}
                                  imageError={null}
                                  onImageChange={(file) => {
                                    const newFiles = [...optionImages];
                                    newFiles[index] = file;
                                    setOptionImages(newFiles);

                                    if (file && optionImageUrls[index]) {
                                      const newUrls = [...optionImageUrls];
                                      newUrls[index] = "";
                                      setOptionImageUrls(newUrls);
                                      setRemovedOptionImageIndices(prev => [...prev, index]);
                                    } else if (!file) {
                                      const newUrls = [...optionImageUrls];
                                      if (newUrls[index]) {
                                        newUrls[index] = "";
                                        setOptionImageUrls(newUrls);
                                        setRemovedOptionImageIndices(prev => [...prev, index]);
                                      }
                                    }
                                  }}
                                />
                              </Box>
                              <Box pt={2}>
                                <IconButton
                                  aria-label="Remove option"
                                  icon={<GoTrashcan />}
                                  onClick={() => {
                                    arrayHelpers.remove(index);
                                    const newImg = [...optionImages];
                                    newImg.splice(index, 1);
                                    setOptionImages(newImg);

                                    const newUrls = [...optionImageUrls];
                                    if (newUrls[index]) {
                                      setRemovedOptionImageIndices(prev => [...prev, index]);
                                    }
                                    newUrls.splice(index, 1);
                                    setOptionImageUrls(newUrls);
                                  }}
                                  variant="ghost" colorScheme="red"
                                />
                              </Box>
                            </HStack>
                          ))}
                          <Button
                            variant="ghost"
                            onClick={() => {
                              arrayHelpers.push("");
                              setOptionImages([...optionImages, null]);
                              setOptionImageUrls([...optionImageUrls, ""]);
                            }}
                            isDisabled={values.mcqSOptions.length >= 6}
                          >
                            Add Option
                          </Button>
                        </VStack>
                      )}
                    </FieldArray>

                    <InputControl label="Answer Justification" name="answerJustification" />

                    {/* Image Uploads */}
                    <FormControl>
                      <FormLabel>Question Image</FormLabel>
                      <Input type="file" accept="image/jpeg,image/png" onChange={(e) => setQuestionImageFile(e.target.files?.[0] || null)} />
                      {question.questionImg && !questionImageFile && <Image src={buildStoredImageUrl(question.questionImg)} maxH="160px" mt={2} borderRadius="md" />}
                      {questionImageFile && <Image src={URL.createObjectURL(questionImageFile)} maxH="160px" mt={2} borderRadius="md" />}
                    </FormControl>

                    <FormControl>
                      <FormLabel>Justification Image</FormLabel>
                      <Input type="file" accept="image/jpeg,image/png" onChange={(e) => setJustificationImageFile(e.target.files?.[0] || null)} />
                      {question.justificationImg && !justificationImageFile && <Image src={buildStoredImageUrl(question.justificationImg)} maxH="160px" mt={2} borderRadius="md" />}
                      {justificationImageFile && <Image src={URL.createObjectURL(justificationImageFile)} maxH="160px" mt={2} borderRadius="md" />}
                    </FormControl>


                    <HStack direction="row" spacing={4} align="center" mt="20px">
                      <SubmitButton colorScheme="teal" isLoading={isLoading}>Save</SubmitButton>
                      <Button as={RoLink} to={`/${role}/dashboard/questions`} variant="ghost">Cancel</Button>
                    </HStack>
                  </VStack>
                </Box>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default UpdateQuestionMCQS;
